import React from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { observer } from "mobx-react";
import classnames from "classnames";

import AccountStatementTransactionDetailsError from "../BetDetails/AccountStatementBetDetailsError";
import { Button } from "@components/buttons";
import PaymentOptionImage from "@components/icons/PaymentOptionImage";
import TransactionReferenceText from "@features/account-statement/shared/TransactionReferenceText";
import { getSVGExtension } from "@utils";
import { useAccountStatementStore } from "@state/hooks/account-statement/useAccountStatementStore";
import { DetailsTypeEnum } from "@state/stores/account-statement/AccountStatementStore";

export default observer(function AccountStatementTransactionDetails() {
	const t = useTranslation().t;
	const store = useAccountStatementStore();

	const { slipDetails, setDetailsType } = store.accountStatementDetailsStore;

	if (slipDetails?.type != DetailsTypeEnum.TransactionDetails) {
		return (
			<AccountStatementTransactionDetailsError
				setDetailsType={setDetailsType}
			/>
		);
	}

	const {
		paymentMethod,
		amount,
		currency,
		transactionStatus,
		ccMaskedPan,
		ccExpiryDate,
		startDate,
		endDate,
		numericalId,
		referenceText,
	} = slipDetails;

	const statusClass = classnames(
		"u-align--v--middle u-type--case--capitalize",
		{
			"u-color--sent--success": transactionStatus.abrv === "success",
			"u-color--text--primary": transactionStatus.abrv === "pending",
			"u-color--sent--muted": transactionStatus.abrv === "canceled",
			"u-color--sent--danger": transactionStatus.abrv === "failed",
		}
	);

	const iconClass = classnames(
		"u-icon u-icon--tny",
		{
			"u-icon--check u-color--sent--success":
				transactionStatus.abrv === "success",
			"u-icon--clock": transactionStatus.abrv === "pending",
			"u-icon--delete u-color--sent--muted":
				transactionStatus.abrv === "canceled",
			"u-icon--remove u-color--sent--danger":
				transactionStatus.abrv === "failed",
		},
		"u-align--v--middle u-mar--right--xtny"
	);

	return (
		<div className="popup">
			<div className="popup__card popup__card--reset-width popup--account-statement__card">
				<div className="popup__header">
					<div className="popup__title">
						{t<string>(
							"ACC_STATEMENT.TRANS_DETAILS.TRANSACTION_DETAILS"
						)}
					</div>

					<Button
						type="button"
						className="btn--square btn--square--xsml btn--primary--inverted"
						iconOnly={true}
						iconClassName="u-icon--sml u-icon--close"
						onClick={() => setDetailsType(0)}
					/>
				</div>
				<div className="popup__body">
					<table className="table table--fixed">
						<thead className="table__head u-type--wgt--regular">
							<tr className="table__head__row table__row">
								<th className="table__head__cell table__cell u-color--text--secondary">
									{t<string>(
										"ACC_STATEMENT.TRANS_DETAILS.TRANSACTION_ID"
									)}
								</th>
								<th className="table__head__cell table__cell u-color--text--primary">
									{numericalId}
								</th>
							</tr>
						</thead>
						<tbody className="table__body">
							<tr className="table__body__row table__row">
								<td className="table__body__cell table__cell">
									{t<string>(
										"ACC_STATEMENT.TRANS_DETAILS.AMOUNT"
									)}
								</td>
								<td className="table__body__cell table__cell u-color--text--primary">
									{amount}
									<TransactionReferenceText
										referenceText={referenceText}
									/>
								</td>
							</tr>
							<tr className="table__body__row table__row">
								<td className="table__body__cell table__cell">
									{t<string>(
										"ACC_STATEMENT.TRANS_DETAILS.CURRENCY"
									)}
								</td>
								<td className="table__body__cell table__cell u-color--text--primary">
									{currency.displayCode}
								</td>
							</tr>
							<tr className="table__body__row table__row">
								<td className="table__body__cell table__cell">
									{t<string>(
										"ACC_STATEMENT.TRANS_DETAILS.STATUS"
									)}
								</td>
								<td className="table__body__cell table__cell u-color--text--primary">
									<i className={iconClass} />
									<span className={statusClass}>
										{transactionStatus.name}
									</span>
								</td>
							</tr>
							<tr className="table__body__row table__row">
								<td className="table__body__cell table__cell">
									{t<string>(
										"ACC_STATEMENT.TRANS_DETAILS.PAYMENT_METHOD"
									)}
								</td>
								<td className="table__body__cell table__cell">
									<div className="card--payment card--payment--borderless u-mar--bottom--reset">
										<div className="card--payment__area u-padd--reset">
											<span className="card--payment__icon">
												<PaymentOptionImage
													className={`card--payment__logo u-icon u-icon--payment u-icon--payment--sml u-icon--payment--${paymentMethod.abrv.toLowerCase()}`}
													svgName={
														paymentMethod.abrv.toLowerCase() +
														getSVGExtension()
													}
												/>
											</span>
											<span className="card--payment__details card--payment__details--centered">
												<span className="card--payment__name u-type--wgt--regular">
													{ccMaskedPan}
												</span>
												<span
													className="card--payment__type"
													title={t<string>(
														"ACC_STATEMENT.TRANS_DETAILS.CARD_EXPIRY"
													)}
												>
													{ccExpiryDate}
												</span>
											</span>
										</div>
									</div>
								</td>
							</tr>
							<tr className="table__body__row table__row">
								<td className="table__body__cell table__cell">
									{t<string>(
										"ACC_STATEMENT.TRANS_DETAILS.START_DATE"
									)}
								</td>
								<td className="table__body__cell table__cell u-color--text--primary">
									{DateTime.fromISO(startDate).toLocaleString(
										DateTime.DATETIME_SHORT_WITH_SECONDS
									)}
								</td>
							</tr>
							<tr className="table__body__row table__row">
								<td className="table__body__cell table__cell">
									{t<string>(
										"ACC_STATEMENT.TRANS_DETAILS.END_DATE"
									)}
								</td>
								<td className="table__body__cell table__cell u-color--text--primary">
									{DateTime.fromISO(endDate).toLocaleString(
										DateTime.DATETIME_SHORT_WITH_SECONDS
									)}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
});
